import { useCallback, useEffect, useState } from 'react'
import { useForm, FieldPath } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { getNow, toApiYmd } from '../../utils/dateUtil'
import { datePropsToNumber, undefinedPropsToOptional } from '../../utils/objectUtil'
import { setFormValueClearError } from '../../utils/reactUtil'
import { NullPropsToUndefinedType, castNonNullable } from '../../utils/typeUtil'
import { translate } from '../../i18n'
import { GetFacilityDto } from '../../dataAccess/webApi/dto/facilitiesDto'
import { GetResidenceCategoryMstDto } from '../../dataAccess/webApi/dto/residenceCategoryMstDto'
import {
  ADDITIONALS,
  getChild,
  ChildrenChoice,
  getChildrenChoices,
  getInterviewedChildrenChoices,
  Child,
  InterviewedChildrenChoice
} from '../common/child'
import { getFacility } from '../common/facility'
import { getResidenceCategoriesAndMstData } from '../common/residenceCategoryMst'
import { getInterviewStatusLabel } from '../common/codeMaster'
import { interviewReservationConfirmationUrl } from '../common/constant/appUrl'
import { Flag, yesNo } from '../common/constant/classification'
import { OperationId } from '../common/constant/operationLog'
import {
  interviewStatus,
  permitFlag,
  availabilityOfApplication,
  interviewAcceptMethod,
  interviewCrossManagementFlag
} from '../common/constant/classification'
import { useErrorHandle } from '../common/error/errorHandler'
import { getAddressStringByPostalCode } from '../common/location'
import { useOperationLog } from '../common/operationLog'
import { showLoading } from '../common/store/slices/application'
import {
  selectInterviewReserveEntry,
  setInterviewReserve,
  setInterviewReserveUpdateDatetime,
  SelectChildren,
} from '../common/store/slices/interviewReserve'
import { getUser } from '../common/user'
import { DocType, TempFile, FacilityInputDetail, getInterview, setStringifyNamesChildInfoList } from '../common/interview'
import { promiseAllConcurrency } from '../../utils/promiseUtil'
import { selectSystemControl } from '../common/store/slices/systemControl'
import { getCategories, getCategorySpecifyMasters, SpecifyMasterType } from '../common/category'
import { executeGetProjects } from '../../dataAccess/webApi/dao/projectsDao'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'
import { CATEGORY_NAME as ALLERGY } from '../../views/components/common/member/childAllergyUnit'
import { CATEGORY_NAME as DISEASE } from '../../views/components/common/member/childDiseaseUnit'
import { CATEGORY_NAMES as EATING_KEYS } from '../../views/components/common/member/childEatingUnit'
import { CATEGORY_NAME as FEBRILE_SEIZURE } from '../../views/components/common/member/childFebrileSeizureUnit'
import { CATEGORY_NAMES as HOSPITAL_ADMISSION_KEYS } from '../../views/components/common/member/childHospitalAdmissionUnit'
import { CATEGORY_NAME as MEDICAL_HISTORY } from '../../views/components/common/member/childMedicalHistoryUnit'
import { CATEGORY_NAMES as PREDISPOSITION_KEYS } from '../../views/components/common/member/childPredispositionUnit'
import { CATEGORY_NAME as SYMPTOM } from '../../views/components/common/member/childSymptomUnit'
import { CATEGORY_NAME as VACCINATION } from '../../views/components/common/member/childVaccinationUnit'
import { executeGetProjectDetail } from '../../dataAccess/webApi/dao/projectsDao'
import { GetProjectDetailDto } from '../../dataAccess/webApi/dto/projectsDto'
import isInAcceptAgeLimit, { FacilityAgeLimit, OutLimitAgeChilds } from '../common/acceptAgeLimit'

/** 最大並列実行数 */
const MAX_WORKER_COUNT = 4

const DEFAULT_FACILITY_ID = '0' // 事業共通お子様情報はfacility_id = 0
const ALLERGY_PROJECT_ID = '1' // アレルギー項目は全事業共通のため事業ID:1
const ADDITIONAL_TABLES = [ADDITIONALS.HOSPITAL_ADMISSION, ADDITIONALS.EATING, ADDITIONALS.PREDISPOSITION]

interface UrlParams {
  facilityId: string
  childId: string
}

interface LocationState {
  /** 取得・入力済み情報から復元を試みる場合true */
  isKeep: boolean
}

interface PageState {
  facility?: NullPropsToUndefinedType<GetFacilityDto>
  uninterviewedChilds: ChildrenChoice[]
  childs: ChildrenChoice[]
  interviewedChildsAndStatus: ChildrenChoice[]
}

export interface Inputs {
  parentName: string
  parentKana: string
  postalCode: string
  address1: string
  address2: string
  buildingNameRoomNumber: string
  residenceCategory: string
  relationship: string
  emergencyContactFlag?: boolean
  children: child[]
}

export type interviewReservationInputKey = FieldPath<Inputs>

export interface child {
  childId: string
  childName: string
  childKana: string
  childGender: string
  childBirthday: Date
  childMedicalHistoryFlag: string
  childMedicalHistory: string
  childAllergyFlag: string
  childAllergy: string
  maternalHandbookNo: string
  note: string
  existingMemberFlag: string
  existingMemberEditFlag: string
  facilityNumber: string
  childUpdateDatetime: string
  childInterviewPermitCount: number
  availabilityOfApplication: string
  childOrig: Child
  tempFile?: TempFile
  facilityInputFields?: { [key: string]: FacilityInputDetail }
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory<LocationState | undefined>()
  const { facilityId } = useParams<UrlParams>()
  const { addOperationLog } = useOperationLog()
  const sysCtrl = useSelector(selectSystemControl)
  const isKeep = !!history.location.state?.isKeep
  const reserveEntry = useSelector(selectInterviewReserveEntry)
  const childBirthdayLimitDate = getNow() // 誕生日入力の上限値
  
  const formMethods = useForm<Inputs>({
    defaultValues: {
    ...(isKeep && reserveEntry && {
      ...reserveEntry,
      children: reserveEntry.children?.map(child => ({
        ...child,
        // ミリ秒から Date オブジェクトに変換
        childBirthday: new Date(child.childBirthday),
      })),
    }),
  },
  })
  const watchAllFields= formMethods.watch()
  const [userInterviewPermitCount, setUserInterviewPermitCount] = useState<number>(0)
  const [childInterviewPermitCount, setChildInterviewPermitCount] = useState<number>(0)
  const [isOpenInterviewChildSelect, setIsOpenInterviewChildSelect] = useState(false)
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false)
  const [state, setState] = useState<PageState>({ uninterviewedChilds: [], childs: [], interviewedChildsAndStatus: [] })
  const [childId, setChildId] = useState('')
  const [residenceCategoryMst, setResidenceCategoryMst] = useState<GetResidenceCategoryMstDto>()
  const [residenceCategories, setResidenceCategories] = useState<{ value: string; label: string }[]>([])
  const [isDisabledAddress1, setIsDisabledAddress1] = useState(true)
  const [outLimitAgeChilds, setOutLimitAgeChilds] = useState<OutLimitAgeChilds[]>([])
  // 緊急連絡先登録有無フラグ
  const [emergencyContactFlag, setEmergencyContactFlag] = useState(false)
  /** カテゴリーマスタ */
  const [category, setCategory] = useState<GetCategoryMstDto[]>()
  const [allergyCategory, setAllergyCategory] = useState<GetCategoryMstDto[]>()
  const [diseaseCategory, setDiseaseCategory] = useState<GetCategoryMstDto[]>()
  const [medicalHistoryCategory, setMedicalHistoryCategory] = useState<GetCategoryMstDto[]>()
  const [symptomCategory, setSymptomCategory] = useState<GetCategoryMstDto[]>()
  const [vaccinationCategory, setVaccinationCategory] = useState<GetCategoryMstDto[]>()
  
  /** カテゴリーマスタをオブジェクト形式で保持する */
  const [categoryMap, setCategoryMap] = useState<{[key:string]: GetCategoryMstDto}>({})
  const [projectMst, setprojectMst] = useState<GetProjectDetailDto>()
  
  let isDisabledResidenceCategory = false
  if ((isKeep && reserveEntry?.isDisabledResidenceCategory) || userInterviewPermitCount > 0) {
    isDisabledResidenceCategory = true
  }
  let isDisabledBirthday = false
  if (childInterviewPermitCount > 0) {
    isDisabledBirthday = true
  }
  
  // 初期表示
  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001, accessData: [{ userIdRegFlag: yesNo.yes, childId }] })
    dispatch(
      showLoading(
        errorHandle(async () => {
          // 在住種別を取得＆表示
          const residenceCategoriesAndMstData = await getResidenceCategoriesAndMstData()
          setResidenceCategoryMst(residenceCategoriesAndMstData.mstData)
          setResidenceCategories(residenceCategoriesAndMstData.categories)
          const facility = await getFacility(facilityId)
          setState({...state, facility})
          const projectMstData: GetProjectDetailDto = await getProjectMstData(facility.projectId)
          setprojectMst(projectMstData)

          // 事業マスタ取得
          const projectMstResp = await executeGetProjects({ projectId: String(facility.projectId) })
          const isAnnualUpdate = projectMstResp.result.length ? projectMstResp.result[0].annualUpdateFlag === yesNo.yes : false
          
          if (!reserveEntry || !reserveEntry.interviewNo) {
            // 新規申込
            if (facility.interviewDailyAcceptFlag === yesNo.yes && facility.interviewAcceptMethod === interviewAcceptMethod.interviewSlots) {
              // 面談申込受付方法：日時の受付を行う
              // 面談受付設定：面談枠で受け付ける
              if (!isKeep && reserveEntry && reserveEntry.childIds) {
                // お子さまの情報を取得
                const facilityWithChilds = await getFacilityWithChilds(facilityId, facility, isAnnualUpdate)
                setState({...state, ...facilityWithChilds, facility})
                await initialize(reserveEntry.childIds, reserveEntry.selectChildren, facility)
              }
            } else {
              // 面談申込受付方法：日時の受付を行わない
              // 面談受付設定：未設定、申込のみ
              if (!(isKeep && reserveEntry)) {
                // お子さま選択ダイアログの情報を取得＆表示
                const facilityWithChilds = await getFacilityWithChilds(facilityId, facility, isAnnualUpdate)
                setState({...state, ...facilityWithChilds, facility})
                setIsOpenInterviewChildSelect(true)
              }
            }
          } else {
            // 予約変更
            if (!isKeep && reserveEntry && reserveEntry.childIds) {
              // お子さまの情報を取得
              const facilityWithChilds = await getFacilityWithChilds(facilityId, facility, isAnnualUpdate)
              setState({...state, ...facilityWithChilds, facility})
              await initializeUpdate(reserveEntry.interviewNo, reserveEntry.childIds, reserveEntry.selectChildren, facility)
            }
          }
          // 項目マスタ取得        
          const categoryMsts = await getCategoryMsts(facility.projectId)
          const category = categoryMsts.category
          setCategory(category)
          setAllergyCategory(categoryMsts.allergyCategory)
          setDiseaseCategory(categoryMsts.diseaseCategory)
          setMedicalHistoryCategory(categoryMsts.medicalHistoryCategory)
          setSymptomCategory(categoryMsts.symptomCategory)
          setVaccinationCategory(categoryMsts.vaccinationCategory)
          const tmpMap: {[key:string]: GetCategoryMstDto} = {}
          category?.forEach(e => tmpMap[e.category] = e)
          setCategoryMap(tmpMap)
          // 緊急連絡先登録有無の取得
          const user = await getUser()
          const emergencyContact = user.emergencyContacts[0]
          const hasValue =
            emergencyContact != null && (
              emergencyContact.name != null && emergencyContact.name !== "" ||
              emergencyContact.kana != null && emergencyContact.kana !== "" ||
              emergencyContact.relationship != null && emergencyContact.relationship !== "" ||
              emergencyContact.company != null && emergencyContact.company !== ""　||
              emergencyContact.company != null && emergencyContact.company !== "" ||
              emergencyContact.tel != null && emergencyContact.tel !== "" ||
              emergencyContact.email != null && emergencyContact.email !== ""
            )
          const emergencyContactFlag = hasValue ? true : false
          setEmergencyContactFlag(emergencyContactFlag)
        })
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
 
  const setResidenceCategory = useCallback((address1: string) => {
    if (
      !isDisabledResidenceCategory 
      && residenceCategoryMst?.triggerKeywordSetInitialValue
      && address1.includes(residenceCategoryMst.triggerKeywordSetInitialValue)
    ) {
      setFormValueClearError(formMethods, 'residenceCategory', residenceCategoryMst.initialValue)
    }
  }, [isDisabledResidenceCategory, residenceCategoryMst, formMethods])
  
  useEffect(() => {
    const subscription = formMethods.watch((values, { name, type }) => {
      if (name === 'address1' && type === 'change') {
        if (values.address1) {
          setResidenceCategory(values.address1)
        }
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setResidenceCategory])
  
  // 初期設定
  const initialize = useCallback(
    async (childIds: string[], selectChildren?: SelectChildren[], facility?: NullPropsToUndefinedType<GetFacilityDto>) => {
      const {
        initialInputs,
        userInterviewPermitCount,
        userUpdateDatetime,
      } = await getInitialInputs(childIds, selectChildren, facility)
      formMethods.reset({
        ...formMethods.getValues(),
        ...initialInputs,
      })
      dispatch(setInterviewReserveUpdateDatetime({ userUpdateDatetime }))
      setUserInterviewPermitCount(userInterviewPermitCount)
      setChildInterviewPermitCount(childInterviewPermitCount)
      setChildId(childId)
  },[dispatch, errorHandle, formMethods])
  
  // 予約変更の初期設定
  const initializeUpdate = useCallback(
    async (
      interviewNo: string,
      childIds: string[],
      selectChildren?: SelectChildren[],
      facility?: NullPropsToUndefinedType<GetFacilityDto>
    ) => {
      const {
        initialInputs,
        userInterviewPermitCount,
        userUpdateDatetime,
        interviewUpdateDatetime,
      } = await getInitialInputsUpdate(interviewNo, childIds, selectChildren, facility)
      formMethods.reset({
        ...formMethods.getValues(),
        ...initialInputs,
      })

      dispatch(setInterviewReserveUpdateDatetime({
        userUpdateDatetime,
        interviewUpdateDatetime
      }))
      setUserInterviewPermitCount(userInterviewPermitCount)
      setChildInterviewPermitCount(childInterviewPermitCount)
      setChildId(childId)
  },[dispatch, errorHandle, formMethods])

  // 次の画面へ
  const goNext = useCallback(
    (data: Inputs) => {

      const matchValueLabels = residenceCategories.filter((element) => {
        return data.residenceCategory === element.value
      })
      const residenceCategoryName = matchValueLabels[0].label

      let applicationChildren = data.children
      .map(child => ({
        ...child,
        childBirthday: child.childBirthday,
        // 事業マスタの施設登録方法（interviewAcceptMethod）が'1'の場合は、アレルギー・既往症項目が非表示の為nullになる
        // その状態を回避する為、DB必須項目に'0'を設定する
        childAllergyFlag: child.childAllergyFlag ?? '0',
        childMedicalHistoryFlag: child.childMedicalHistoryFlag ?? '0',
        // 未チェックは空文字なので、0に変換する
        existingMemberFlag: (child.existingMemberFlag && child.existingMemberFlag.length) ? child.existingMemberFlag : '0',
      }))
      .map ((child) => {
        return datePropsToNumber(child)
      })

      // JSON.parseと再設定が必要（dataに設定されたままsliceに設定するとエラーになる）
      applicationChildren = JSON.parse(JSON.stringify(applicationChildren));
      applicationChildren.map((childInfo) => {
        if (childInfo.facilityInputFields) {
          const tempField = {...childInfo.facilityInputFields};
          childInfo.facilityInputFields = {}
          for (const [key, value] of Object.entries(tempField)) {
            if (!value) continue
            childInfo.facilityInputFields[key] = value
          }
        }
      })

      const confirmationData = {
        address1: data.address1,
        address2: data.address2,
        buildingNameRoomNumber: data.buildingNameRoomNumber,
        parentKana: data.parentKana,
        parentName: data.parentName,
        postalCode: data.postalCode,
        relationship: data.relationship,
        residenceCategory: data.residenceCategory,

        children: setStringifyNamesChildInfoList(applicationChildren),
      }

      dispatch(
        setInterviewReserve({
          ...confirmationData,
          facilityId,
          childIds: reserveEntry?.childIds,
          childId,
          residenceCategoryName,
          selectChildren: reserveEntry?.selectChildren,
          interviewNo: reserveEntry?.interviewNo,
          interviewDatetimes: reserveEntry?.interviewDatetimes,
          isDisabledResidenceCategory,
        })
      )
      // 戻るで表示した際に取得・入力済み情報から復元を試みる為に履歴に保管
      history.replace({ ...history.location, state: { isKeep: true } })
      history.push(interviewReservationConfirmationUrl.url())
    },
    [dispatch, history, facilityId, childId, addOperationLog, residenceCategories, isDisabledResidenceCategory, emergencyContactFlag]
  )

  const autoCompleteAddress = useCallback(async () => {
    const { postalCode } = formMethods.getValues()
    try {
      const address = await getAddressStringByPostalCode(postalCode)
      if (address) {
        formMethods.setValue('address1', address)
        setResidenceCategory(address)
        setIsDisabledAddress1(true)
      } else {
        setIsDisabledAddress1(false)
      }
    } catch {
      // 取得できなかった場合は何もしない
    }
  }, [formMethods, setResidenceCategory])

  // お子さま選択ダイアログのボタン押下
  const onCloseInterviewChildSelect = useCallback(
    (isCancel: boolean, childIds?: string[]) => {
      dispatch(
        showLoading({
          process: 
          errorHandle(async () => {
            if (isCancel || !childIds) {
              // 選択をキャンセルした場合
              setIsOpenInterviewChildSelect(false)
              history.goBack()
            } else {
              // お子さまを選択した場合
              const filteredChildIds = childIds.filter(childId => childId !== '')
              if (filteredChildIds.length <= 0) {
                return
              }
              setIsOpenInterviewChildSelect(false)
              await validateAcceptAgeLimit(filteredChildIds)
              await initialize(castNonNullable(filteredChildIds), [], state.facility)
            }
          }),
          isHiddenMain: false,
        })
      )

    },
    [history, initialize, state]
  )

  const validateAcceptAgeLimit = async (filteredChildIds: string[]) => {
    const childs = await promiseAllConcurrency(
      filteredChildIds.map((childId) => async () => {
        const child = await getChild(childId)
        return child
      }),
      MAX_WORKER_COUNT
    )
    const facility = await getFacility(facilityId)

    if (facility.pmInterviewBeforeDayFlag === Flag.ON && facility.interviewBeforeDayFlag === Flag.ON) {
      if (facility.acceptStartAge != null && facility.acceptStartAge != null) {
        const facilityAgeLimit: FacilityAgeLimit = {
          isMinGrade: facility.acceptStartAgeGradeFlag === Flag.OFF,
          isMaxGrade: facility.acceptEndAgeGradeFlag === Flag.OFF,
          minAgeYear: facility.acceptStartAge,
          maxAgeYear: facility.acceptEndAge != null ? facility.acceptEndAge : null,
          minAgeMonth: facility.acceptStartMonth != null ? facility.acceptStartMonth : null,
          maxAgeMonth: facility.acceptEndMonth != null ? facility.acceptEndMonth : null,
          minAgeClass: facility.acceptStartAgeClass != null ? facility.acceptStartAgeClass : null,
          maxAgeClass: facility.acceptEndAgeClass != null ? facility.acceptEndAgeClass : null,
          beforeDay: facility.interviewBeforeDay ? facility.interviewBeforeDay : 0
        }

        const outLimitAgeChilds: OutLimitAgeChilds[] = childs.map((child: Child) => {
          const isIn = isInAcceptAgeLimit(facilityAgeLimit, child.birthday)

          return {name: child.name, ...isIn}
        })

        setOutLimitAgeChilds(outLimitAgeChilds)
      }
    }
  }

  const closeSubmitDialog = useCallback(async () => {
    setIsSubmitDialogOpen(false)
  }, [])
  
  const openSubmitDialog = useCallback(async () => {
    setIsSubmitDialogOpen(true)
  }, [])

  const closeAgeLimitDialog = useCallback(() => {
    setOutLimitAgeChilds([])
    setIsOpenInterviewChildSelect(true)
  }, [])
  
  const hasError = useCallback(
    (data: Inputs) => {
      
      // 1人以上申込みがあるかチェック
      const applicationChildrenFilter = data.children.filter((child) => {
        return child.availabilityOfApplication === availabilityOfApplication.apply
      })
      // 申込みが0人の場合はエラーメッセージを表示する
      if (!applicationChildrenFilter.length) {
        for (let idx = 0; idx < data.children.length; idx++) {
          formMethods.setError(`children.${idx}.availabilityOfApplication`, {
            message: translate('interviewReservationForm.error.overOneApply'),
          })
        }
        return true
      }

      // 緊急連絡先が未登録の場合はエラーメッセージを表示する
      if (!emergencyContactFlag) {
        const errorMessageDiv = document.getElementById('errorMessage')
        if (errorMessageDiv) {
          errorMessageDiv.textContent = translate('interviewReservationForm.error.emergencyContact')
          errorMessageDiv.style.color = '#c53030'
      
          // エラーメッセージの箇所までスクロール
          errorMessageDiv.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        return true
      }
      
      return false
  },
  [emergencyContactFlag]
  )
  
  const onSubmit = useCallback((data: Inputs) => {
    addOperationLog({ operationId: OperationId.OP_00000025 })

    if (hasError(data)) return

    const hasInterviewRequestNotice = !!(state.facility?.interviewRequestNotice?.length)

    if (hasInterviewRequestNotice) {
        openSubmitDialog()
        return
    }

    goNext(data)
  },
  [addOperationLog, hasError]
  )

  return {
    formMethods,
    autoCompleteAddress,
    onSubmit,
    userInterviewPermitCount,
    childInterviewPermitCount,
    childBirthdayLimitDate,
    isOpenInterviewChildSelect,
    onCloseInterviewChildSelect,
    isDisabledResidenceCategory,
    isDisabledBirthday,
    residenceCategories,
    ...state,
    isDisabledAddress1,
    emergencyContactFlag,
    watchAllFields,
    sysCtrl,
    projectMst,
    category,
    allergyCategory,
    diseaseCategory,
    medicalHistoryCategory,
    symptomCategory,
    vaccinationCategory,
    categoryMap,
    goNext,
    isSubmitDialogOpen,
    closeSubmitDialog,
    outLimitAgeChilds,
    closeAgeLimitDialog,
  }
}

/**
 * INPUT情報初期設定（新規予約）
 */
const getInitialInputs = async (
  childIds: string[],
  selectChildren?: SelectChildren[],
  facility?: NullPropsToUndefinedType<GetFacilityDto>
) => {
  const user = await getUser()
  const childs = await promiseAllConcurrency(
    childIds.map((childId) => async () => {
      return await getChild(childId, DEFAULT_FACILITY_ID, ADDITIONAL_TABLES)
    }),
    MAX_WORKER_COUNT
  )

  const children:any[] = []
  childs.forEach((child:Child) => {
    const selectChild = selectChildren?.find((v: any) => {
      return v.childId === child.childId
    })
    
    children.push({
      childId: child.childId,
      childName: child.name,
      childKana: child.kana,
      childGender: child.gender,
      childBirthday: child.birthday,
      maternalHandbookNo: child.maternalHandbookNo,
      childUpdateDatetime: child.updateDatetime,
      childInterviewPermitCount: child.interviewPermitCount,
      availabilityOfApplication: yesNo.yes,
      note: facility?.interviewCitizenNoteEntryTemplate ?? '',
      existingMemberFlag: selectChild?.existingMemberFlag ?? '',
      existingMemberEditFlag: selectChild?.existingMemberFlag === Flag.ON ? Flag.ON : Flag.OFF,
      childOrig: child,
      tempFile: {
        files: [],
        hasSubmitted: Flag.OFF,
      },
      ...createFacilityInputFields(facility)
    })
  })
  return {
    initialInputs: undefinedPropsToOptional({
      parentName: user.name,
      parentKana: user.kana,
      postalCode: user.postalCode,
      address1: user.address1,
      address2: user.address2,
      buildingNameRoomNumber: user.buildingNameRoomNumber,
      residenceCategory: user.residenceCategory,
      relationship: user.relationship,
      children: children,
    }),
    userInterviewPermitCount: user.interviewPermitCount,
    //お子様ごとの誕生日の表示非表示に利用しているので、個別で持たせる
    userUpdateDatetime: user.updateDatetime,
  }
}

/**
 * INPUT情報初期設定（予約変更）
 */
const getInitialInputsUpdate = async (
  interviewNo: string,
  childIds: string[],
  selectChildren?: SelectChildren[],
  facility?: NullPropsToUndefinedType<GetFacilityDto>
) => {
  const user = await getUser()
  const interview = await getInterview(interviewNo)
  const child = await getChild(childIds[0], DEFAULT_FACILITY_ID, ADDITIONAL_TABLES)

  const tempFiles = createTempFiles(interview)
  const tempFile: TempFile = {
    files: tempFiles,
    hasSubmitted: tempFiles.length >= 1 ? Flag.ON : Flag.OFF,
  }

  const selectChildInfo = selectChildren?.filter((selectChild: SelectChildren) => {
    return selectChild.childId === child.childId
  })

  const children:any[] = []
  children.push({
    childId: child.childId,
    childName: child.name,
    childKana: child.kana,
    childGender: child.gender,
    childBirthday: child.birthday,
    maternalHandbookNo: child.maternalHandbookNo,
    childUpdateDatetime: child.updateDatetime,
    childInterviewPermitCount: child.interviewPermitCount,
    availabilityOfApplication: yesNo.yes,
    childMedicalHistoryFlag: interview.childMedicalHistoryFlag,
    childMedicalHistory: interview.childMedicalHistory,
    childAllergyFlag: interview.childAllergyFlag,
    childAllergy: interview.childAllergy,
    note: interview.note,
    existingMemberFlag: selectChildInfo ? selectChildInfo[0]?.existingMemberFlag : interview.existingMemberFlag,
    existingMemberEditFlag: (selectChildInfo && selectChildInfo[0]?.existingMemberFlag) ?? Flag.OFF,
    facilityNumber: interview.facilityNumber,
    childOrig: child,
    tempFile: tempFile,
    ...createFacilityInputFields(facility, interview)
  })

  return {
    initialInputs: undefinedPropsToOptional({
      parentName: user.name,
      parentKana: user.kana,
      postalCode: user.postalCode,
      address1: user.address1,
      address2: user.address2,
      buildingNameRoomNumber: user.buildingNameRoomNumber,
      residenceCategory: user.residenceCategory,
      relationship: user.relationship,
      children: children,
    }),
    userInterviewPermitCount: user.interviewPermitCount,
    //お子様ごとの誕生日の表示非表示に利用しているので、個別で持たせる
    userUpdateDatetime: user.updateDatetime,
    interviewUpdateDatetime: interview.interviewsUpdateDatetime,
  }
}

const createTempFiles = (interview: any) =>{
  const tempFiles: DocType[] = [];

  if(interview.tempDoc1Name){
    tempFiles.push({
      fileName: interview.tempDoc1Name,
      tempUploadKey: interview.tempDoc1,
      delFlg:Flag.OFF
    })
  }
  if(interview.tempDoc2Name){
    tempFiles.push({
      fileName: interview.tempDoc2Name,
      tempUploadKey: interview.tempDoc2,
      delFlg:Flag.OFF
    })
  }
  if(interview.tempDoc3Name){
    tempFiles.push({
      fileName: interview.tempDoc3Name,
      tempUploadKey: interview.tempDoc3,
      delFlg:Flag.OFF
    })
  }
  if(interview.tempDoc4Name){
    tempFiles.push({
      fileName: interview.tempDoc4Name,
      tempUploadKey: interview.tempDoc4,
      delFlg:Flag.OFF
    })
  }
  if(interview.tempDoc5Name){
    tempFiles.push({
      fileName: interview.tempDoc5Name,
      tempUploadKey: interview.tempDoc5,
      delFlg:Flag.OFF
    })
  }

  return tempFiles
}

/**
 * 施設別面談時入力項目
 */
const createFacilityInputFields = (facility?: NullPropsToUndefinedType<GetFacilityDto>, interview?: any) => {
  const facilityInputFields:{ [key: string]: FacilityInputDetail } = {}

  if (facility && facility.interviewFacilityInputFieldsDisplayFlag === Flag.ON) {
    // 任意項目の初期値を設定
    facility.interviewFacilityInputFields?.forEach((field: any) => {
      const key = field.id
      facilityInputFields[String(key)] = {
        id: null,
        answer: "",
        updateDatetime: null,
        facilityInterviewInputFieldId: key,
        order: field.order,
      }
    })

    // 面談情報がある場合は書き換える（予約変更）
    if (interview) {
      interview.facilityInputAnswers?.forEach((answer: any) => {
        const key = answer.facilityInterviewInputFieldId
        if (facilityInputFields[String(key)]) {
          facilityInputFields[key] = {
            id: answer.id,
            answer: answer.answer,
            updateDatetime: answer.updateDatetime,
            facilityInterviewInputFieldId: key,
            order: facilityInputFields[key].order
          }
        }
      })
    }
  }

  return {
    facilityInputFields
  }
}

/**
 * お子さま一覧取得（お子さま選択ダイアログで使用）
 */
async function getFacilityWithChilds(facilityId: string, facility: any, isAnnualUpdate: boolean) {
  const [allChilds, interviewedChilds] = await Promise.all([
    getChildrenChoices(),
    getInterviewedChildrenChoices(facilityId, yesNo.yes),
  ])

  //面談許可時の利用許可範囲
  const pmInterviewCrossManagementFlag = facility?.interviewCrossManagementFlag ?? interviewCrossManagementFlag.byFacility

  //A761にて、キャンセル済お子様にラジオボタンを表示して再登録を可能にする対応を行いました。
  //本来SQLで修正をするところですが、お子様選択一覧画面に表示されるキャンセル済お子様のラベルに
  //「（キャンセル済）」のステータスを表示する対応が今後発生する可能性があり、その場合SQLの修正は不要になってしまうため画面での対応としました。

  //interviewedChilds（面談済みお子様）から状態：キャンセル済を除外する
  let exceptForChildsWithCanceledInterview
  if (isAnnualUpdate) {
    // 年度更新暫定対応 許可済でも再度面談申込できるように
    exceptForChildsWithCanceledInterview = interviewedChilds.filter((v) => v.status !== interviewStatus.canceled && v.permitFlag !== permitFlag.permitted) 
  } else {
    exceptForChildsWithCanceledInterview = interviewedChilds.filter((v) => v.status !== interviewStatus.canceled) 
  }
  const interviewedChildIdSet = new Set(exceptForChildsWithCanceledInterview.map(({ value }) => value))
  const uninterviewedChilds = allChilds.filter(({ value }) => !interviewedChildIdSet.has(value))
  const childs = interviewedChilds.filter((v) => v.permitFlag === permitFlag.permitted)

  //1つでも利用許可したら同一事業内の施設で利用可能とする場合の重複対応
  if (pmInterviewCrossManagementFlag === interviewCrossManagementFlag.sameBusiness) {
    //重複情報
    const valueCounts: Record<string, any> = {}
    exceptForChildsWithCanceledInterview.forEach((item: any) => {
      //カウント
      valueCounts[item.value] = (valueCounts[item.value] || 0) + 1
      //自施設が含まれるか
      if (item.facilityId === facilityId) {
        valueCounts['myFacility' + item.value] = true
      }
    })

    //重複するvalue（お子さまID）は自施設の情報を優先する
    const result: InterviewedChildrenChoice[] = []
    const doDuplicatelist: string[] = []
    exceptForChildsWithCanceledInterview.forEach((item: any) => {
      if (valueCounts[item.value] > 1) {
        //重複あり
        if (!doDuplicatelist.includes(item.value)) {
          //重複未処理
          if (valueCounts['myFacility' + item.value] && item.facilityId === facilityId) {
            //自施設が含まれる場合は自施設情報を設定
            //（例）自：確定済、他：確定済 ⇒ 自
            result.push(item)
            doDuplicatelist.push(item.value)
          } else if (!valueCounts['myFacility' + item.value]) {
            //自施設が含まれない場合は他施設情報を設定
            //（例）他A：確定済、他B：確定済 ⇒ 他（A or B どちらかでOK）
            result.push(item)
            doDuplicatelist.push(item.value)
          }
        }
      } else {
        //重複なしはそのまま
        result.push(item)
      }
    })

    //重複を除いた情報に置き換える
    exceptForChildsWithCanceledInterview = result
  }

  const interviewedChildsAndStatus = exceptForChildsWithCanceledInterview.map((v) => {
    let label = v.label
    if (v.status) {
      if (pmInterviewCrossManagementFlag === interviewCrossManagementFlag.sameBusiness && v.facilityId !== facilityId) {
        //1つでも利用許可したら同一事業内の施設で利用可能とする
        label = v.label + '（' + getInterviewStatusLabel(v.status)
                + '：' + translate('interviewReservationForm.label.sameBusiness') + '）'
      } else {
        //上記以外（施設毎に利用許可を行う）
        label = v.label + '（' + getInterviewStatusLabel(v.status) + '）'
      }
    }
    return { value: v.value, label: label }
  })

  return {
    uninterviewedChilds,
    childs,
    interviewedChildsAndStatus,
  }
}

async function getCategoryMsts(projectId: string) {
  const CATEGORY_KEYS = [
    'childrenGender',
    'childrenBloodType',
    'maternalHandbookNo',
    'allergyFlag',
    'allergyCauses1',
    'allergySymptoms1',
    'allergyTreatments1',
    'allergyConcerns1',
    'allergyCauses2',
    'allergySymptoms2',
    'allergyTreatments2',
    'allergyConcerns2',
    'allergyCauses3',
    'allergySymptoms3',
    'allergyTreatments3',
    'allergyConcerns3',
    'brothersSistersFlag',
    'multipleBirthsFlag',
    'medicalHistoryContent',
  ]

  const [
    category,
    allergyCategory,
    diseaseCategory,
    medicalHistoryCategory,
    symptomCategory,
    vaccinationCategory,
  ] = await Promise.all([
    getCategories({projectId: projectId, categories: [...CATEGORY_KEYS, ...EATING_KEYS, FEBRILE_SEIZURE, ...HOSPITAL_ADMISSION_KEYS, ...PREDISPOSITION_KEYS]}),
    getCategories({projectId: ALLERGY_PROJECT_ID, categories: [ALLERGY]}),
    getCategorySpecifyMasters({projectId: projectId, category: DISEASE, masterType: SpecifyMasterType.DISEASE}),
    getCategorySpecifyMasters({projectId: projectId, category: MEDICAL_HISTORY, masterType: SpecifyMasterType.MEDICAL_HISTORY}),
    getCategorySpecifyMasters({projectId: projectId, category: SYMPTOM, masterType: SpecifyMasterType.SYMPTOM}),
    getCategorySpecifyMasters({projectId: projectId, category: VACCINATION, masterType: SpecifyMasterType.VACCINATION})
  ])
  
  return {
    category,
    allergyCategory,
    diseaseCategory,
    medicalHistoryCategory,
    symptomCategory,
    vaccinationCategory,
  }
}

const getProjectMstData = async (projectId: string)=>{
  const projectMstData =  await executeGetProjectDetail(projectId, toApiYmd(Date.now()))
  return projectMstData.result
}